/**
 * Directiva `v-reveal` — anima elementos al entrar en el viewport.
 *
 * Añade la clase `reveal` al montar y `reveal-in` al intersectar. El
 * estado inicial/animación se define en CSS (cada vista o componente lo
 * estila según su estética).
 *
 * Uso:
 *   <div v-reveal class="...">...</div>
 *   <div v-reveal="{ delay: 200 }">...</div>   // delay en ms
 *
 * La transición se define en `src/styles/main.css` bajo `.reveal`.
 */
import type { Directive } from 'vue'

type RevealValue = { delay?: number } | undefined

const observers = new WeakMap<Element, IntersectionObserver>()

export const vReveal: Directive<HTMLElement, RevealValue> = {
  mounted(el, binding) {
    el.classList.add('reveal')
    const delay = binding.value?.delay ?? 0
    if (delay) el.style.transitionDelay = `${delay}ms`

    if (typeof IntersectionObserver === 'undefined') {
      el.classList.add('reveal-in')
      return
    }

    const io = new IntersectionObserver(
      (entries, obs) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('reveal-in')
            obs.disconnect()
          }
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -6% 0px' },
    )
    io.observe(el)
    observers.set(el, io)
  },
  unmounted(el) {
    observers.get(el)?.disconnect()
    observers.delete(el)
  },
}
